import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { FileSignature, Plus, Trash2, Eye, CheckCircle, Clock, AlertCircle, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

export default function SignableDocumentsCompanyPage() {
  const [documents, setDocuments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file: null,
    sendToAll: true,
    selectedWorkers: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, workersRes] = await Promise.all([
        API.get('/signable-documents'),
        API.get('/workers')
      ]);
      setDocuments(docsRes.data);
      setWorkers(workersRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.file) {
      setError('Selecciona un archivo');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('title', formData.title);
      if (formData.description) data.append('description', formData.description);
      
      const targetWorkers = formData.sendToAll ? null : formData.selectedWorkers;
      if (targetWorkers) data.append('target_workers', JSON.stringify(targetWorkers));
      
      await API.post('/signable-documents', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setDialogOpen(false);
      setFormData({ title: '', description: '', file: null, sendToAll: true, selectedWorkers: [] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await API.delete(`/signable-documents/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/signable-documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const viewDetail = async (doc) => {
    try {
      const response = await API.get(`/signable-documents/${doc.id}`);
      setDetailDoc(response.data);
    } catch (err) {
      console.error('Error fetching document details:', err);
    }
  };

  const toggleWorker = (workerId) => {
    setFormData(prev => ({
      ...prev,
      selectedWorkers: prev.selectedWorkers.includes(workerId)
        ? prev.selectedWorkers.filter(id => id !== workerId)
        : [...prev.selectedWorkers, workerId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-100 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signable-documents-company-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900" >
            Documentos para Firmar
          </h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Envía contratos y documentos a tus trabajadores</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" data-testid="create-signable-doc-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Documento
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Enviar Documento para Firma</DialogTitle>
              <DialogDescription>
                Sube un documento (contrato, acuerdo, etc.) para que los trabajadores lo firmen.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Título del documento</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Contrato de trabajo 2024"
                  required
                  data-testid="signable-doc-title"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Descripción (opcional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción o instrucciones para el trabajador"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Archivo (PDF)</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  className="cursor-pointer"
                  data-testid="signable-doc-file"
                />
              </div>
              
              <div className="space-y-3 pt-2">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Destinatarios</Label>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sendToAll"
                    checked={formData.sendToAll}
                    onCheckedChange={(checked) => setFormData({ ...formData, sendToAll: checked, selectedWorkers: [] })}
                  />
                  <label htmlFor="sendToAll" className="text-sm font-medium text-gray-700">
                    Enviar a todos los trabajadores
                  </label>
                </div>
                
                {!formData.sendToAll && (
                  <div className="border border-orange-100 rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
                    {workers.length === 0 ? (
                      <p className="text-sm text-gray-500">No hay trabajadores</p>
                    ) : (
                      workers.map((worker) => (
                        <div key={worker.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`worker-${worker.id}`}
                            checked={formData.selectedWorkers.includes(worker.id)}
                            onCheckedChange={() => toggleWorker(worker.id)}
                          />
                          <label htmlFor={`worker-${worker.id}`} className="text-sm text-gray-700">
                            {worker.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" disabled={uploading}>
                  {uploading ? 'Enviando...' : 'Enviar Documento'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Documents List */}
      <div className="bg-white border border-orange-100 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-orange-100 bg-[#FFF8F4]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Documento</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fecha</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-center">Firmados</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                  No hay documentos enviados
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id} className="hover:bg-orange-50/50" data-testid={`signable-doc-row-${doc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-50 rounded flex items-center justify-center">
                        <FileSignature className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{doc.title}</p>
                        {doc.description && <p className="text-xs text-gray-500 truncate max-w-[200px]">{doc.description}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm">
                    {new Date(doc.created_at).toLocaleDateString('es-ES')}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={doc.signed_count === doc.total_count ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {doc.signed_count} / {doc.total_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => viewDetail(doc)}
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteId(doc.id)}
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detailDoc} onOpenChange={() => setDetailDoc(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{detailDoc?.title}</DialogTitle>
            <DialogDescription>{detailDoc?.description}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => detailDoc && handleDownload(detailDoc)}>
                <Eye className="w-4 h-4 mr-2" />
                Ver documento
              </Button>
              <div className="text-sm text-gray-500">
                {detailDoc?.signed_count} de {detailDoc?.total_count} firmados
              </div>
            </div>
            
            <div className="border-t border-orange-100 pt-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Estado de firmas</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {detailDoc?.signatures_list?.map((sig) => (
                  <div key={sig.worker_id} className="flex items-center justify-between p-2 bg-emerald-50 rounded">
                    <span className="text-sm text-gray-700">{sig.worker_name}</span>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs text-gray-500">
                        {new Date(sig.signed_at).toLocaleDateString('es-ES')}
                      </span>
                    </div>
                  </div>
                ))}
                {detailDoc?.target_workers?.filter(id => !detailDoc?.signatures_list?.find(s => s.worker_id === id)).map((workerId) => (
                  <div key={workerId} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                    <span className="text-sm text-gray-700">Trabajador pendiente</span>
                    <Clock className="w-4 h-4 text-amber-500" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el documento y todas las firmas asociadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
